import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  ElderDataToolbarComponent,
  ElderFormFieldDenseDirective,
  ElderSelectComponent,
  ElderSelectValueDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToastService,
  Pageable,
  SelectionModelPopupDirective,
} from '@elderbyte/ngx-starter'
import { Router } from '@angular/router'
import { BehaviorSubject, Observable } from 'rxjs'
import { PropertySpec } from '../property-spec'
import { PropertyService } from '../../property.service'
import { FilterBuilder } from '../../../orders/filter-builder'
import { Property, PropertyKind } from '../../property'
import { PropertyUtil } from '../../property-util'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { MatCheckbox } from '@angular/material/checkbox'
import { PropertyBrowserComponent } from '../../property-browser/property-browser.component'
import { FormsModule } from '@angular/forms'
import { PropertySelectDirective } from '../../property-select/property-select.directive'
import { MatFormField } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { AsyncPipe } from '@angular/common'

@Component({
  selector: 'app-property-spec-table',
  templateUrl: './property-spec-table.component.html',
  styleUrls: ['./property-spec-table.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatFormField,
    ElderFormFieldDenseDirective,
    ElderSelectComponent,
    PropertySelectDirective,
    FormsModule,
    ElderSelectValueDirective,
    SelectionModelPopupDirective,
    PropertyBrowserComponent,
    MatCheckbox,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class PropertySpecTableComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _specs$: BehaviorSubject<PropertySpec[]> = new BehaviorSubject<PropertySpec[]>(
    []
  )

  @Input()
  public propertyKinds: PropertyKind[]

  @Output()
  public specsChange: EventEmitter<PropertySpec[]> = new EventEmitter<PropertySpec[]>()

  public PropertyUtil = PropertyUtil

  private propertyService = inject(PropertyService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get specs$(): Observable<PropertySpec[]> {
    return this._specs$.asObservable()
  }

  public get specs(): PropertySpec[] {
    return this._specs$.getValue()
  }

  @Input()
  public set specs(specs: PropertySpec[]) {
    this._specs$.next(specs)

    if (specs) {
      this.propertyService
        .findAllPaged(new Pageable(0, 100), [
          FilterBuilder.ids(specs.map((spec) => spec.propertyId)),
        ])
        .subscribe({
          next: (properties) => this.populate(specs, properties.content),
          error: (err) => this.toastService.error('Failed to load property specs!', err),
        })
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onNew(event: Event): void {
    this.addConstraint(new PropertySpec())
  }

  public onRemove(constraints: PropertySpec[]): void {
    this.removeProperties(constraints)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private addConstraint(toAdd: PropertySpec) {
    const properties = [...this.specs]
    properties.push(toAdd)
    this.specs = properties
    this.specsChange.emit(this.specs)
  }

  private removeProperties(toRemove: PropertySpec[]) {
    const properties = [...this.specs]
    toRemove.forEach((c) => properties.splice(properties.indexOf(c), 1))
    this.specs = properties
    this.specsChange.emit(this.specs)
  }

  private populate(specs: PropertySpec[], properties: Property[]): void {
    for (const spec of specs) {
      const property = properties.find((prop) => prop.id === spec.propertyId)

      if (property) {
        spec.property = property
      } else {
        this.log.warn('Failed to populate. Could not find Property with id ' + spec.propertyId)
      }
    }
  }
}
